/**
 * Full-screen screenshot annotation editor.
 * Tools: pen (freehand), arrow, rectangle, text, color picker, stroke width.
 * Undo/redo stack. Exports annotated image as a File.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, Divider, IconButton,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, TextField,
} from '@mui/material';
import {
  Draw, NorthEast, CropSquare, TextFields,
  Undo, Redo, Delete, RestartAlt,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { COLORS, WIDTHS, renderStroke } from './annotatorUtils';
import type { AnnotationTool, Stroke } from './annotatorUtils';

interface Props {
  open: boolean;
  imageUrl: string;
  onSave: (file: File) => void;
  onCancel: () => void;
}

export const ScreenshotAnnotator = ({ open, imageUrl, onSave, onCancel }: Props) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<AnnotationTool>('pen');
  const [color, setColor] = useState('#ff0000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [redoStack, setRedoStack] = useState<Stroke[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [current, setCurrent] = useState<Stroke | null>(null);
  const [textInput, setTextInput] = useState<{ x: number; y: number } | null>(null);
  const [textValue, setTextValue] = useState('');
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!open || !imageUrl) return;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const maxW = window.innerWidth - 80;
      const maxH = window.innerHeight - 160;
      const s = Math.min(maxW / img.width, maxH / img.height, 1);
      setScale(s);
      setCanvasSize({ w: Math.round(img.width * s), h: Math.round(img.height * s) });
      setStrokes([]);
      setRedoStack([]);
    };
    img.src = imageUrl;
  }, [open, imageUrl]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const all = current ? [...strokes, current] : strokes;
    for (const s of all) renderStroke(ctx, s);
  }, [strokes, current]);

  useEffect(() => { redraw(); }, [redraw]);

  const getPos = (e: React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (tool === 'text') { setTextInput(getPos(e)); setTextValue(''); return; }
    setDrawing(true);
    const pos = getPos(e);
    setCurrent(tool === 'pen'
      ? { tool, color, width: strokeWidth, points: [pos] }
      : { tool, color, width: strokeWidth, start: pos, end: pos });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drawing || !current) return;
    const pos = getPos(e);
    setCurrent((prev) => prev
      ? tool === 'pen'
        ? { ...prev, points: [...(prev.points || []), pos] }
        : { ...prev, end: pos }
      : prev);
  };

  const handleMouseUp = () => {
    if (!drawing || !current) return;
    setDrawing(false);
    setStrokes((prev) => [...prev, current]);
    setRedoStack([]);
    setCurrent(null);
  };

  const handleTextConfirm = () => {
    if (textInput && textValue.trim()) {
      setStrokes((prev) => [...prev, { tool: 'text', color, width: strokeWidth, text: textValue.trim(), pos: textInput }]);
      setRedoStack([]);
    }
    setTextInput(null);
  };

  const undo = () => setStrokes((prev) => {
    if (!prev.length) return prev;
    setRedoStack((r) => [...r, prev[prev.length - 1]]);
    return prev.slice(0, -1);
  });
  const redo = () => setRedoStack((prev) => {
    if (!prev.length) return prev;
    setStrokes((s) => [...s, prev[prev.length - 1]]);
    return prev.slice(0, -1);
  });

  const handleSave = () => {
    const img = imgRef.current;
    if (!img) return;
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    const ctx = c.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    const sf = 1 / scale;
    for (const s of strokes) renderStroke(ctx, s, sf);
    c.toBlob((blob) => {
      if (blob) onSave(new File([blob], `annotated-screenshot-${Date.now()}.png`, { type: 'image/png' }));
    }, 'image/png');
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={false} fullScreen
      PaperProps={{ sx: { bgcolor: '#1a1a1a', color: '#fff' } }}
      sx={{ zIndex: 1600 }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: '#111', borderBottom: '1px solid #333', flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 1 }}>Annotate Screenshot</Typography>
        <Divider orientation="vertical" flexItem sx={{ borderColor: '#444' }} />
        <ToggleButtonGroup size="small" exclusive value={tool} onChange={(_, v) => v && setTool(v)}
          sx={{ '& .MuiToggleButton-root': { px: 1.2, py: 0.4, color: '#aaa', borderColor: '#444', '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.25), color: '#fff' } } }}>
          <ToggleButton value="pen"><Tooltip title="Pen"><Draw sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
          <ToggleButton value="arrow"><Tooltip title="Arrow"><NorthEast sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
          <ToggleButton value="rect"><Tooltip title="Rectangle"><CropSquare sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
          <ToggleButton value="text"><Tooltip title="Text"><TextFields sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
        </ToggleButtonGroup>
        <Divider orientation="vertical" flexItem sx={{ borderColor: '#444' }} />
        <Box sx={{ display: 'flex', gap: 0.3 }}>
          {COLORS.map((c) => (
            <Box key={c} onClick={() => setColor(c)} sx={{
              width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
              border: color === c ? '2px solid #fff' : '2px solid transparent',
            }} />
          ))}
        </Box>
        <Divider orientation="vertical" flexItem sx={{ borderColor: '#444' }} />
        <Box sx={{ display: 'flex', gap: 0.3 }}>
          {WIDTHS.map((w) => (
            <Tooltip key={w} title={`${w}px`}>
              <IconButton size="small" onClick={() => setStrokeWidth(w)} sx={{ color: strokeWidth === w ? '#fff' : '#666', p: 0.5 }}>
                <Box sx={{ width: w + 8, height: w + 8, borderRadius: '50%', bgcolor: strokeWidth === w ? color : '#555' }} />
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <Divider orientation="vertical" flexItem sx={{ borderColor: '#444' }} />
        <Tooltip title="Undo"><IconButton size="small" onClick={undo} disabled={!strokes.length} sx={{ color: '#aaa' }}><Undo /></IconButton></Tooltip>
        <Tooltip title="Redo"><IconButton size="small" onClick={redo} disabled={!redoStack.length} sx={{ color: '#aaa' }}><Redo /></IconButton></Tooltip>
        <Tooltip title="Clear all"><IconButton size="small" onClick={() => { setRedoStack([...redoStack, ...strokes]); setStrokes([]); }} disabled={!strokes.length} sx={{ color: '#aaa' }}><Delete /></IconButton></Tooltip>
        <Tooltip title="Reset"><IconButton size="small" onClick={() => { setStrokes([]); setRedoStack([]); }} sx={{ color: '#aaa' }}><RestartAlt /></IconButton></Tooltip>
      </Box>

      {/* Canvas */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', p: 2, position: 'relative' }}>
        <Box sx={{ position: 'relative' }}>
          <canvas ref={canvasRef} width={canvasSize.w} height={canvasSize.h}
            onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
            style={{ cursor: tool === 'text' ? 'text' : 'crosshair', display: 'block', borderRadius: 4, boxShadow: '0 0 20px rgba(0,0,0,0.5)' }} />
          {textInput && (
            <TextField autoFocus size="small" value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleTextConfirm(); if (e.key === 'Escape') setTextInput(null); }}
              onBlur={handleTextConfirm}
              sx={{
                position: 'absolute', left: textInput.x, top: textInput.y,
                '& .MuiInputBase-input': { color, fontWeight: 'bold', fontSize: `${Math.max(16, strokeWidth * 4)}px`, p: 0.5 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(color, 0.5) },
              }} />
          )}
        </Box>
      </Box>

      <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#111', borderTop: '1px solid #333' }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', color: '#aaa' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Save Annotated Screenshot
        </Button>
      </DialogActions>
    </Dialog>
  );
};
